import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { catchError } from "rxjs/operators";

import {
  IndustryUserTabSettings,
  IndustryUserIndustrySettings,
} from "src/app/store/models/industry-page.model";
import { PeriodUserSettingObj } from "@va/core-library/period-filter";
import { AppService } from "../app.service";

@Injectable({
  providedIn: "root",
})
export class ComparativeAnalysisService {
  apiEP: string = window.apiUrl;

  constructor(
    private http: HttpClient,
    private appService: AppService,
  ) {}

  /**
   * returns count of slected tickers depending on the industry, subgroup, marketcap, region
   */
  fetchCompaniesCount(data): Observable<any> {
    return this.http.post(`${this.apiEP}/api/get_cluster_company_count`, data);
  }

  fetchCompaniesList(data): Observable<any> {
    data["need_ticker_info"] = true;
    if (data.watchlist_id) {
      data["type"] = "watchlist";
    } else {
      data["type"] = "industry";
    }
    return this.http.post(`${this.apiEP}/api/get_cluster_company_count`, data);
  }

  fetchComparativeAnalysisMenuData(comp_type: string): Observable<any> {
    let paramUrl = "";
    paramUrl += `${comp_type}=true`;
    return this.http.get(
      `${this.apiEP}/api/get_comparative_menu_data?${paramUrl}`,
    );
  }

  fetchMarketAndGeoData(payload?) {
    if (!payload) {
      payload = {
        market_cap: [-1],
        region_id: [-1],
      };
    }
    return this.http.post(`${this.apiEP}/api/get_market_geo_details`, payload);
  }

  fetchComparativeAnalysisMeta(payload) {
    return this.http.post(`${this.apiEP}/comps/get_comparative_meta`, payload);
  }

  getStdLineItems(
    tickers: Array<string>,
    showSuggestedLineItems = false,
    currency = "USD",
    showValRatio = false,
  ): Observable<any> {
    const reqObj: {
      tickers: Array<string>;
      show_suggested_lines: boolean;
      currency?: string;
      show_val_ratio?: boolean;
    } = { tickers, show_suggested_lines: showSuggestedLineItems };

    if (currency) {
      reqObj.currency = currency;
    }
    if (showValRatio) {
      reqObj.show_val_ratio = showValRatio;
    }
    return this.http.post(`${this.apiEP}/comps/get_std_line_items`, reqObj);
  }

  getTotalLogic(lineId: number) {
    return this.http.get(
      `${this.apiEP}/industry_page/total_logic_check?line_id=${lineId}`,
    );
  }

  updateLeftPanel(getData) {
    if (getData) {
      this.appService.updateLeftPanelSource.next({ dataRefresh: "true" });
    } else {
      this.appService.updateLeftPanelSource.next({ dataRefresh: "false" });
    }
  }

  getLineItemDetails(
    uniquesliId,
    industryId,
    currency,
    ticker,
    isChart = false,
  ): Observable<any> {
    let new_uniqueSliId = "";
    if (uniquesliId && uniquesliId.length) {
      new_uniqueSliId = `&uniquesliid=${uniquesliId.join("&uniquesliid=")}`;
    }
    let url = `${this.apiEP}/comps/get_lineitem_details?ticker=${ticker}${new_uniqueSliId}&industry_id=${industryId}&currency=${currency}`;
    if (isChart) {
      url += `&is_chart=${isChart}`;
    }
    return this.http.get(url);
  }

  saveUserSettings(
    regions: Array<string>,
    marketCap: Array<string>,
    currency: string,
    period: PeriodUserSettingObj,
    industryId: number,
    removeBlankRows: boolean,
  ): Observable<any> {
    const reqObj = {
      jsondata: {
        market_cap: marketCap,
        region: regions,
        currency: currency,
        remove_blanks: removeBlankRows,
      },
      userindustrydata: {
        period,
      },
      industryid: industryId,
    };
    return this.http.post(
      `${this.apiEP}/industry_page/save_user_settings`,
      reqObj,
    );
  }

  /**
   * It makes post call to save user's tab settings
   * @param {string} tabId - tab id
   * @param {IndustryUserTabSettings} data - selected info of filters
   */
  saveUserIndustryTabSettings(
    tabId,
    data: IndustryUserTabSettings,
  ): Observable<any> {
    const reqObj = {
      [tabId]: data,
    };
    return this.http.post(
      `${this.apiEP}/industry_page/save_user_industry_tab_settings`,
      reqObj,
    );
  }

  /**
   * It makes post call to save user's industry settings
   * @param {IndustryUserIndustrySettings} data - selected info of market cap, region
   */
  saveUserIndustrySettings(
    data: IndustryUserIndustrySettings,
  ): Observable<any> {
    return this.http.post(
      `${this.apiEP}/industry_page/save_user_industry_settings`,
      data,
    );
  }

  downloadIndustryExport(
    params: string,
    subIndustryId = -1,
    regionIds = [-1],
    marketCapIds = [-1],
    countryIds = [],
  ): Observable<any> {
    const subIndustry = `&subindustry_id=${subIndustryId || -1}`;
    let region = "";
    if (regionIds && regionIds.length) {
      region = `&region_id=${regionIds.join("&region_id=")}`;
    }
    let country = "";
    if (countryIds && countryIds.length) {
      country = `&country_id=${countryIds.join("&country_id=")}`;
    }
    let marketCap = "";
    if (marketCapIds && marketCapIds.length) {
      marketCap = `&market_cap=${marketCapIds.join("&market_cap=")}`;
    }
    return this.http.get(
      `${this.apiEP}/addin/export?${params}${subIndustry}${region}${marketCap}${country}`,
    );
  }

  downloadComparativeExport(payload, templateType, tabName, export_type) {
    let url = `${this.apiEP}/addin/export?template_type=${templateType}&page_name=${tabName}&export_type=${export_type}`;
    if (tabName === "Valuation") {
      url += "&val_grid_type=comparative";
    } else if (tabName === "Revision" || tabName === "Surprise") {
      url += "&rev_sup_grid_type=comparative";
    }
    return this.http.post(url, payload);
  }

  downloadComparativeTPRExport(payload, params) {
    return this.http.post(`${this.apiEP}/addin/export?${params}`, payload);
  }

  getUserCompId(params): Observable<any> {
    let urlParam = "";
    if (!(Object.keys(params).length === 0 && params.constructor === Object)) {
      urlParam = `?accepted_comps=${params["accepted_comps"]}&shared_id=${params["shared_id"]}&is_cc=true`;
    }
    return this.http.get(`${this.apiEP}/comps/prepare_cc_default${urlParam}`);
  }

  getCompanyPeers(
    tickers: Array<string>,
    sliIds: Array<number>,
    limit = 10,
    peersBySli = false,
    ignoreMpt = false,
  ): Observable<any> {
    if (sliIds && sliIds.length) {
      return this.http
        .get(
          `${this.apiEP}/charts_page/get_company_peers?ticker=${tickers.join(
            "&ticker=",
          )}&uniquesliid=${sliIds.join(
            "&uniquesliid=",
          )}&limit=${limit}&peers_by_usli=${peersBySli}`,
        )
        .pipe(catchError((error) => of(error)));
    }
    return this.http
      .get(
        `${this.apiEP}/charts_page/get_company_peers?ticker=${tickers.join(
          "&ticker=",
        )}&limit=${limit}&ignore_mpt=${ignoreMpt}`,
      )
      .pipe(catchError((error) => of(error)));
  }

  getRecentlyVisitedCompanies(): Observable<any> {
    return this.http.get(
      `${this.apiEP}/comps/get_recently_visited_active_companies`,
    );
  }

  saveUnsavedComps(payload): Observable<any> {
    return this.http.post(
      `${this.apiEP}/comps/add_update_custom_comps`,
      payload,
    );
  }

  updateCompsName(payload): Observable<any> {
    return this.http.post(
      `${this.apiEP}/comps/add_update_custom_comps`,
      payload,
    );
  }

  updateCustomComps(payload): Observable<any> {
    return this.http.post(
      `${this.apiEP}/comps/add_update_comps_tab_data`,
      payload,
    );
  }

  getTopTenTickers(payload): Observable<any> {
    return this.http.post(`${this.apiEP}/comps/get_top_tickers`, payload);
  }

  deleteCustomComps(payload): Observable<any> {
    return this.http.post(`${this.apiEP}/comps/delete_custom_comps`, payload);
  }

  duplicateCustomComps(payload): Observable<any> {
    return this.http.post(
      `${this.apiEP}/comps/duplicate_custom_comps`,
      payload,
    );
  }

  getIndustryTabData(params): Observable<any> {
    return this.http.get(
      `${this.apiEP}/industry_page/get_industry_data?${params}`,
    );
  }

  getComparativeMenuTreeIdDetails(comp_type, id): Observable<any> {
    return this.http.get(
      `${this.apiEP}/api/get_comparative_menu_id_details?comp_type=${comp_type}&id=${id}`,
    );
  }
}
